import { useState, useEffect, useCallback } from 'react';
import { TrainerAutocomplete } from '../common/TrainerAutocomplete';
import { FormInput } from '../common/FormInput';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { ActionButtonGroup } from '../common/ActionButtonGroup';
import { BallSelector } from '../common/BallSelector';
import { Modal } from '../common/Modal';
import { Pagination } from '../common/Pagination';
import { Card } from '../common/Card';
import { TypeBadge } from '../common/TypeBadge';
import { AttributeBadge } from '../common/AttributeBadge';
import api from '../../services/api';
import bazarService from '../../services/bazarService';
import speciesService from '../../services/speciesService';
import type { SpeciesImageMap } from '../../services/speciesService';
import { extractErrorMessage } from '../../utils/errorUtils';
import type {
  TownTrainer,
  BazarMonster,
  BazarAdoptMonsterProps
} from './types';

/**
 * BazarAdoptMonster - Adopt forfeited monsters from the bazar
 * Browse and claim monsters forfeited by other trainers
 */
export function BazarAdoptMonster({
  className = '',
  onAdoptComplete
}: BazarAdoptMonsterProps) {
  // Monsters and pagination
  const [monsters, setMonsters] = useState<BazarMonster[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 24
  });

  // Species images cache
  const [speciesImages, setSpeciesImages] = useState<SpeciesImageMap>({});

  // User's trainers for adoption
  const [userTrainers, setUserTrainers] = useState<TownTrainer[]>([]);

  // Adoption modal
  const [selectedMonster, setSelectedMonster] = useState<BazarMonster | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState<TownTrainer | null>(null);
  const [monsterName, setMonsterName] = useState('');
  const [adoptLoading, setAdoptLoading] = useState(false);
  const [adoptError, setAdoptError] = useState('');
  const [adoptSuccess, setAdoptSuccess] = useState('');

  // Fetch available monsters
  const fetchMonsters = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', pagination.limit.toString());

      const response = await api.get(`/town/bazar/monsters?${params.toString()}`);

      setMonsters(response.data.monsters || []);
      setPagination({
        page: response.data.page || 1,
        totalPages: response.data.totalPages || 1,
        total: response.data.total || 0,
        limit: pagination.limit
      });
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load bazar monsters'));
      setMonsters([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  // Initial load
  useEffect(() => {
    fetchMonsters(1);
    bazarService.getUserTrainers().then(data => {
      setUserTrainers(data.trainers || data || []);
    }).catch(() => setUserTrainers([]));
  }, [fetchMonsters]);

  // Fetch species images when monsters change
  useEffect(() => {
    if (monsters.length === 0) return;

    const allSpecies = new Set<string>();
    for (const m of monsters) {
      if (m.species1) allSpecies.add(m.species1);
      if (m.species2) allSpecies.add(m.species2);
      if (m.species3) allSpecies.add(m.species3);
    }

    const needed = [...allSpecies].filter(s => !speciesImages[s]);
    if (needed.length === 0) return;

    speciesService.getSpeciesImages(needed).then(imageMap => {
      setSpeciesImages(prev => ({ ...prev, ...imageMap }));
    }).catch(() => {});
  }, [monsters, speciesImages]);

  // Get species list for a monster
  const getSpeciesList = useCallback((monster: BazarMonster) => {
    return [monster.species1, monster.species2, monster.species3].filter(Boolean) as string[];
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    fetchMonsters(page);
  }, [fetchMonsters]);

  // Handle per-page change
  const handlePerPageChange = useCallback((newLimit: number) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  }, []);

  // Get species display string
  const getSpeciesDisplay = useCallback((monster: BazarMonster) => {
    const species = [monster.species1, monster.species2, monster.species3]
      .filter(Boolean)
      .join(' / ');
    return species || 'Unknown';
  }, []);

  // Get types array
  const getTypes = useCallback((monster: BazarMonster) => {
    return [monster.type1, monster.type2, monster.type3, monster.type4, monster.type5]
      .filter(Boolean) as string[];
  }, []);

  // Open adoption modal
  const handleMonsterClick = useCallback((monster: BazarMonster) => {
    setSelectedMonster(monster);
    setSelectedTrainer(null);
    setMonsterName(monster.name || '');
    setAdoptError('');
    setAdoptSuccess('');
    setShowModal(true);
  }, []);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedMonster(null);
    setSelectedTrainer(null);
    setMonsterName('');
    setAdoptError('');
    setAdoptSuccess('');
  }, []);

  // Handle adoption
  const handleAdopt = useCallback(async () => {
    if (!selectedMonster || !selectedTrainer) return;

    if (!monsterName.trim()) {
      setAdoptError('Please enter a name for your new monster.');
      return;
    }

    try {
      setAdoptLoading(true);
      setAdoptError('');

      await api.post('/town/bazar/adopt/monster', {
        bazarMonsterId: selectedMonster.id,
        trainerId: selectedTrainer.id,
        newName: monsterName.trim()
      });

      setAdoptSuccess(`Successfully adopted ${monsterName}!`);
      onAdoptComplete?.(selectedMonster, selectedTrainer.id);

      // Refresh monsters list after a short delay
      setTimeout(() => {
        handleCloseModal();
        fetchMonsters(pagination.page);
      }, 1500);
    } catch (err) {
      setAdoptError(extractErrorMessage(err, 'Failed to adopt monster'));
    } finally {
      setAdoptLoading(false);
    }
  }, [selectedMonster, selectedTrainer, monsterName, pagination.page, fetchMonsters, handleCloseModal, onAdoptComplete]);

  return (
    <div className={`bazar-adopt-monster ${className}`}>
      <div className="bazar-adopt-monster__header">
        <h2 className="bazar-adopt-monster__title">
          <i className="fas fa-paw"></i> Adopt Monsters
        </h2>
        <p className="bazar-adopt-monster__description">
          Browse monsters forfeited by other trainers and give them a new home.
        </p>
      </div>

      {/* Content */}
      <div className="bazar-adopt-monster__content">
        {loading ? (
          <div className="bazar-adopt-monster__loading">
            <LoadingSpinner />
            <p>Loading available monsters...</p>
          </div>
        ) : error ? (
          <ErrorMessage message={error} />
        ) : monsters.length === 0 ? (
          <div className="bazar-adopt-monster__empty">
            <i className="fas fa-paw"></i>
            <p>No monsters available for adoption.</p>
          </div>
        ) : (
          <>
            <div className="bazar-adopt-monster__grid">
              {monsters.map(monster => (
                <Card
                  key={monster.id}
                  className="bazar-adopt-monster__card"
                  onClick={() => handleMonsterClick(monster)}
                  hoverable
                >
                  <div className="bazar-adopt-monster__card-image">
                    {(() => {
                      const species = getSpeciesList(monster);
                      const hasSpeciesImages = species.some(s => speciesImages[s]?.image_url);

                      if (monster.img_link) {
                        return (
                          <img
                            src={monster.img_link}
                            alt={monster.name || getSpeciesDisplay(monster)}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/images/monsters/default.png';
                            }}
                          />
                        );
                      }

                      if (hasSpeciesImages) {
                        return (
                          <div className={`bazar-adopt-monster__species-images bazar-adopt-monster__species-images--${species.filter(s => speciesImages[s]?.image_url).length}`}>
                            {species.map((s, i) => {
                              const imgData = speciesImages[s];
                              if (!imgData?.image_url) return null;
                              return (
                                <img
                                  key={i}
                                  src={imgData.image_url}
                                  alt={s}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              );
                            })}
                          </div>
                        );
                      }

                      return (
                        <div className="bazar-adopt-monster__card-placeholder">
                          <i className="fas fa-question"></i>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="bazar-adopt-monster__card-info">
                    <h4 className="bazar-adopt-monster__card-name">
                      {getSpeciesDisplay(monster)}
                    </h4>
                    <p className="bazar-adopt-monster__card-species">
                      {monster.name || 'Unnamed'}
                    </p>
                    <div className="bazar-adopt-monster__card-types">
                      {getTypes(monster).map((type, idx) => (
                        <TypeBadge key={idx} type={type} size="sm" />
                      ))}
                    </div>
                    {monster.attribute && (
                      <div className="bazar-adopt-monster__card-attribute">
                        <AttributeBadge attribute={monster.attribute} size="sm" />
                      </div>
                    )}
                    {monster.level && (
                      <span className="bazar-adopt-monster__card-level">
                        Lv. {monster.level}
                      </span>
                    )}
                    {monster.ball && (
                      <BallSelector
                        selectedBall={monster.ball}
                        onBallChange={() => {}}
                        compact
                      />
                    )}
                  </div>
                </Card>
              ))}
            </div>

            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              perPage={pagination.limit}
              onPerPageChange={handlePerPageChange}
            />
          </>
        )}
      </div>

      {/* Adoption Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title="Adopt Monster"
        size="medium"
      >
        {selectedMonster && (
          <div className="bazar-adopt-monster__modal-content">
            {adoptSuccess ? (
              <div className="bazar-adopt-monster__success">
                <div className="bazar-adopt-monster__success-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h3>Success!</h3>
                <p>{adoptSuccess}</p>
              </div>
            ) : (
              <>
                {/* Monster Preview */}
                <div className="bazar-adopt-monster__preview">
                  <div className="bazar-adopt-monster__preview-image">
                    {(() => {
                      const species = getSpeciesList(selectedMonster);
                      const hasSpeciesImages = species.some(s => speciesImages[s]?.image_url);

                      if (selectedMonster.img_link) {
                        return (
                          <img
                            src={selectedMonster.img_link}
                            alt={selectedMonster.name || getSpeciesDisplay(selectedMonster)}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/images/monsters/default.png';
                            }}
                          />
                        );
                      }

                      if (hasSpeciesImages) {
                        return (
                          <div className={`bazar-adopt-monster__species-images bazar-adopt-monster__species-images--${species.filter(s => speciesImages[s]?.image_url).length}`}>
                            {species.map((s, i) => {
                              const imgData = speciesImages[s];
                              if (!imgData?.image_url) return null;
                              return (
                                <img
                                  key={i}
                                  src={imgData.image_url}
                                  alt={s}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              );
                            })}
                          </div>
                        );
                      }

                      return (
                        <div className="bazar-adopt-monster__preview-placeholder">
                          <i className="fas fa-question"></i>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="bazar-adopt-monster__preview-info">
                    <h3>{getSpeciesDisplay(selectedMonster)}</h3>
                    <p className="bazar-adopt-monster__preview-species">
                      {selectedMonster.name || 'Unnamed Monster'}
                    </p>
                    <div className="bazar-adopt-monster__preview-types">
                      {getTypes(selectedMonster).map((type, idx) => (
                        <TypeBadge key={idx} type={type} size="sm" />
                      ))}
                    </div>
                    {selectedMonster.attribute && (
                      <AttributeBadge attribute={selectedMonster.attribute} size="sm" />
                    )}
                    {selectedMonster.level && (
                      <p className="bazar-adopt-monster__preview-level">
                        Level {selectedMonster.level}
                      </p>
                    )}
                    {selectedMonster.ball && (
                      <BallSelector
                        selectedBall={selectedMonster.ball}
                        onBallChange={() => {}}
                        compact
                      />
                    )}
                  </div>
                </div>

                {/* Trainer Selection */}
                <div className="bazar-adopt-monster__trainer-select">
                  <label className="form-label">Select Trainer to Adopt</label>
                  <TrainerAutocomplete
                    trainers={userTrainers}
                    onSelectTrainer={(trainer) => setSelectedTrainer(trainer as TownTrainer | null)}
                    value={selectedTrainer?.id ?? null}
                    placeholder="Search for your trainer..."
                  />
                </div>

                {/* Monster Name */}
                <div className="bazar-adopt-monster__name-input">
                  <FormInput
                    label="Monster Name"
                    value={monsterName}
                    onChange={(e) => setMonsterName(e.target.value)}
                    placeholder="Enter a name for your new monster..."
                    required
                  />
                </div>

                {adoptError && <ErrorMessage message={adoptError} />}

                {/* Actions */}
                <ActionButtonGroup align="end" className="mt-md">
                  <button
                    className="button secondary"
                    onClick={handleCloseModal}
                    disabled={adoptLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className="button primary"
                    onClick={handleAdopt}
                    disabled={adoptLoading || !selectedTrainer || !monsterName.trim()}
                  >
                    {adoptLoading ? (
                      <>
                        <LoadingSpinner size="sm" message="" />
                        Adopting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-heart"></i> Adopt
                      </>
                    )}
                  </button>
                </ActionButtonGroup>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default BazarAdoptMonster;
